import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  VStack,
  Button,
  Text,
  useToast,
  Icon,
  HStack,
  GridItem,
  Heading,
  useDisclosure,
  Flex,
  Switch,
  Badge,
} from "@chakra-ui/react";

import { useNavigate, useLocation } from "react-router-dom";
import {
  CarFrontIcon,
  Home,
  LogInIcon,
  LogOutIcon,
  MessageCircleIcon,
  SendIcon,
  ShoppingBag,
  User2,
  Settings,
  HouseIcon,
  Sun,
  Moon,
  Check,
  LayoutGrid,
  Bell,
  BriefcaseBusiness,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";

import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter } from "react-icons/fa";
import NotifyModal from "../Modals/NotifyModal";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { FiCpu } from "react-icons/fi";
import { scrollPageToTop } from "../../utils/scrollPageToTop";


export type NavItem = {
  id: number;
  name: string;
  icon: any;
  path: string;
  color: string;
};

export const navItems: NavItem[] = [
  {
    id: 1,
    name: "Dashboard",
    icon: Home,
    path: "/dashboard",
    color: "#2563EB",
  },
  { id: 3, name: "Shop", icon: ShoppingBag, path: "/shop", color: "#14B8A6" },
  { id: 7, name: "Travel", icon: SendIcon, path: "/hike", color: "#F59E0B" },
  {
    id: 8,
    name: "Delivery",
    icon: CarFrontIcon,
    path: "/delivery/request",
    color: "#8B5CF6",
  },
  {
    id: 9,
    name: "Car Hire",
    icon: CarFrontIcon,
    path: "/hire/cars",
    color: "#8B5CF6",
  },
  {
    id: 10,
    name: "Accomodation",
    icon: HouseIcon,
    path: "/book/accommodation",
    color: "#EC4899",
  },
  {
    id: 11,
    name: "Explore",
    icon: LayoutGrid,
    path: "/explore",
    color: "#14B8A6",
  },
  {
    id: 12,
    name: "Manage",
    icon: LayoutGrid,
    path: "/manage",
    color: "#6366F1",
  },
  {
    id: 15,
    name: "Inbox",
    icon: Bell,
    path: "/notifications",
    color: "#7C3AED",
  },
  { id: 13, name: "Profile", icon: User2, path: "/profile", color: "#06B6D4" },
  {
    id: 14,
    name: "Settings",
    icon: Settings,
    path: "/settings",
    color: "#64748B",
  },
  // {
  //   id: 15,
  //   name: "AI Studio",
  //   path: "/admin/ai-studio-test",
  //   icon: FiCpu,
  //   color: "#F59E0B",
  // },
];

const NOTIFICATIONS_API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/notifications`;

const hexToRgba = (hex: string, alpha: number) => {
  const cleaned = hex.replace("#", "");
  if (cleaned.length !== 6) return `rgba(100,116,139,${alpha})`;

  const bigint = parseInt(cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const SideBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { theme, themeName, toggleTheme } = useCustomTheme();

  const [themeSaving, setThemeSaving] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const isDark = theme.name === "dark";

  const pageText = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;

  const shellBg = theme.gradients?.shellBg || theme.surface;
  const cardBg = theme.surface;

  const borderColor = theme.border;

  const panelShadow = theme.shadowMd;
  const cardShadow = theme.shadowSm;

  const hoverBg = theme.hover;
  const activeBg = theme.gradients?.tabHikesSelected || theme.selected;

  const cardsBorderRadii = "8px";

  const defaultIconColor = isDark
    ? theme.text
    : theme.brand?.deepSlate || theme.text;

  const fetchUnreadNotificationCount = async () => {
    if (!user?.uid) {
      setUnreadNotificationCount(0);
      return;
    }

    try {
      const res = await axios.get(`${NOTIFICATIONS_API}/unread-count`, {
        params: { userId: user.uid },
      });

      setUnreadNotificationCount(Number(res.data?.count || 0));
    } catch (error) {
      console.error("Failed to fetch unread notifications:", error);
      setUnreadNotificationCount(0);
    }
  };

  useEffect(() => {
    fetchUnreadNotificationCount();

    const interval = window.setInterval(() => {
      fetchUnreadNotificationCount();
    }, 30000);

    return () => window.clearInterval(interval);
  }, [user?.uid]);

  const renderContainedIcon = (
    IconComponent: React.ElementType,
    color: string,
    active = false,
    size = 16,
  ) => (
    <Flex
      w="30px"
      h="30px"
      borderRadius={cardsBorderRadii}
      align="center"
      justify="center"
      bg={hexToRgba(color, active ? 0.2 : 0.14)}
      border={`1px solid ${hexToRgba(color, active ? 0.34 : 0.22)}`}
      flexShrink={0}
      transition="all 0.2s ease"
      boxShadow={active ? theme.shadowSm : "none"}
      position="relative"
      overflow="visible"
    >
      <Icon
        as={IconComponent}
        boxSize={`${size}px`}
        color={color}
        fill="transparent"
        strokeWidth={2.2}
      />
    </Flex>
  );

  const handleThemeToggle = async () => {
    setThemeSaving(true);
    try {
      await toggleTheme();
      toast({
        title: `Theme changed to ${themeName === "dark" ? "light" : "dark"}`,
        status: "success",
        duration: 1800,
      });
    } catch (error) {
      console.error("Theme update failed:", error);
      toast({
        title: "Failed to update theme",
        status: "error",
        duration: 3000,
      });
    } finally {
      setThemeSaving(false);
    }
  };

  const handleAuthAction = async () => {
    if (!user) {
      navigate("/login");
      scrollPageToTop();
      return;
    }

    await logout();
    toast({
      title: "Sign Out",
      description: "You are now logged out!",
      status: "success",
      duration: 8000,
      isClosable: true,
    });
    navigate("/dashboard");
    scrollPageToTop();
  };

  return (
    <Box
      width="240px"
      height="90vh"
      p={3}
      position="fixed"
      display="flex"
      flexDirection="column"
      bg={shellBg}
      color={pageText}
      border={`1px solid ${borderColor}`}
      boxShadow={panelShadow}
      backdropFilter={isDark ? "blur(8px)" : undefined}
    >
      <Box
        flex="1"
        overflowY="auto"
        pr={1}
        sx={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            background: theme.borderStrong,
            borderRadius: { cardsBorderRadii },
          },
        }}
      >
        <VStack spacing={2} align="stretch">
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            const isNotifications = item.name === "Inbox";
            const unreadLabel =
              unreadNotificationCount > 99
                ? "99+"
                : String(unreadNotificationCount);

            return (
              <Button
                key={item.id}
                onClick={() => { navigate(item.path); scrollPageToTop(); }}
                leftIcon={
                  <Box position="relative" overflow="visible">
                    {renderContainedIcon(item.icon, item.color, isActive)}
                    {isNotifications && unreadNotificationCount > 0 && (
                      <Badge
                        position="absolute"
                        top="-6px"
                        right="-8px"
                        minW="18px"
                        h="18px"
                        px="4px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius={cardsBorderRadii}
                        bg="red.500"
                        color="white"
                        fontSize="0.65rem"
                        fontWeight="700"
                        lineHeight="1"
                        zIndex={2}
                        boxShadow="sm"
                      >
                        {unreadLabel}
                      </Badge>
                    )}
                  </Box>
                }
                justifyContent="flex-start"
                variant="ghost"
                fontWeight="600"
                fontSize="sm"
                height="46px"
                borderRadius={cardsBorderRadii}
                border={`1px solid ${
                  isActive ? hexToRgba(item.color, 0.24) : "transparent"
                }`}
                bg={isActive ? activeBg : "transparent"}
                color={isActive ? pageText : mutedText}
                boxShadow={isActive ? cardShadow : "none"}
                _hover={{
                  bg: isActive ? activeBg : hoverBg,
                  color: pageText,
                  transform: "translateY(-1px)",
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.2s ease"
                marginTop="0"
                px={3}
              >
                <Flex
                  width="100%"
                  justifyContent="space-between"
                  align="center"
                >
                  <Text>{item.name}</Text>
                  <HStack spacing={2}>
                    {isNotifications && unreadNotificationCount > 0 && (
                      <Badge
                        colorScheme="red"
                        borderRadius={cardsBorderRadii}
                        px={2}
                        fontSize="0.7rem"
                      >
                        {unreadLabel}
                      </Badge>
                    )}
                    {isActive && (
                      <Icon as={Check} boxSize={4} color={item.color} />
                    )}
                  </HStack>
                </Flex>
              </Button>
            );
          })}

          <Box
            mt={2}
            p={3}
            borderRadius={cardsBorderRadii}
            bg={cardBg}
            border={`1px solid ${borderColor}`}
            boxShadow={cardShadow}
          >
            <HStack justify="space-between" align="center">
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="700" color={pageText}>
                  Theme
                </Text>
                <Text fontSize="xs" color={subtleText}>
                  {themeName === "dark" ? "Dark mode" : "Light mode"}
                </Text>
              </VStack>

              <HStack spacing={2}>
                <Flex
                  w="30px"
                  h="30px"
                  borderRadius={cardsBorderRadii}
                  align="center"
                  justify="center"
                  bg={
                    themeName === "dark"
                      ? hexToRgba("#8B5CF6", 0.14)
                      : hexToRgba("#F59E0B", 0.14)
                  }
                  border={`1px solid ${
                    themeName === "dark"
                      ? hexToRgba("#8B5CF6", 0.22)
                      : hexToRgba("#F59E0B", 0.22)
                  }`}
                >
                  {themeName === "dark" ? (
                    <Moon size={16} color="#8B5CF6" />
                  ) : (
                    <Sun size={16} color="#F59E0B" />
                  )}
                </Flex>

                <Switch
                  isChecked={themeName === "dark"}
                  onChange={handleThemeToggle}
                  colorScheme="teal"
                  isDisabled={themeSaving}
                />
              </HStack>
            </HStack>
          </Box>

          <Button
            onClick={handleAuthAction}
            leftIcon={renderContainedIcon(
              user ? LogOutIcon : LogInIcon,
              user ? "#EF4444" : "#14B8A6",
              false,
            )}
            justifyContent="flex-start"
            variant="ghost"
            fontWeight="600"
            fontSize="sm"
            borderRadius={cardsBorderRadii}
            height="46px"
            border="1px solid transparent"
            color={mutedText}
            _hover={{
              bg: hoverBg,
              color: pageText,
              transform: "translateY(-1px)",
            }}
            _active={{ transform: "translateY(0)" }}
            transition="all 0.2s ease"
            marginTop="0"
          >
            <Text>{user ? "Log out" : "Log in"}</Text>
          </Button>

          <Button
            onClick={onOpen}
            justifyContent="flex-start"
            variant="ghost"
            fontWeight="600"
            fontSize="sm"
            borderRadius={cardsBorderRadii}
            height="46px"
            color={mutedText}
            _hover={{
              bg: hoverBg,
              color: pageText,
              transform: "translateY(-1px)",
            }}
            _active={{ transform: "translateY(0)" }}
            transition="all 0.2s ease"
            marginTop="0"
          >
            {renderContainedIcon(MessageCircleIcon, "#06B6D4", false)}
            <Text ml="10px">Ask Us</Text>
          </Button>
          {/* Careers */}
<Button
  onClick={() => { navigate("/careers"); scrollPageToTop(); }}
  justifyContent="flex-start"
  variant="ghost"
  fontWeight="600"
  fontSize="sm"
  borderRadius={cardsBorderRadii}
  height="46px"
  color={mutedText}
  leftIcon={renderContainedIcon(
    BriefcaseBusiness,
    "#10897e",
    location.pathname.startsWith("/careers")
  )}
  _hover={{
    bg: hoverBg,
    color: pageText,
    transform: "translateY(-1px)",
  }}
  _active={{ transform: "translateY(0)" }}
  transition="all 0.2s ease"
  marginTop="0"
>
  Careers
</Button>

          <Box
            mt={4}
            p={4}
            borderRadius={cardsBorderRadii}
            bg={cardBg}
            border={`1px solid ${borderColor}`}
            boxShadow={cardShadow}
          >
            <HStack spacing={2} mb={4}>
              <Flex
                w="34px"
                h="34px"
                borderRadius={cardsBorderRadii}
                align="center"
                justify="center"
                bg={hexToRgba("#0077B5", 0.12)}
                border={`1px solid ${hexToRgba("#0077B5", 0.2)}`}
                cursor="pointer"
                transition="all 0.2s ease"
                _hover={{ transform: "translateY(-1px)" }}
              >
                <a
                  href="https://www.linkedin.com/company/pickdengu/?viewAsMember=true"
                  target="blank"
                >
                  {" "}
                  <Icon
                    as={FaLinkedin as React.ElementType}
                    boxSize={4}
                    color="#0077B5"
                  />
                </a>
              </Flex>

              <Flex
                w="34px"
                h="34px"
                borderRadius={cardsBorderRadii}
                align="center"
                justify="center"
                bg={hexToRgba("#145DBF", 0.12)}
                border={`1px solid ${hexToRgba("#145DBF", 0.2)}`}
                cursor="pointer"
                transition="all 0.2s ease"
                _hover={{ transform: "translateY(-1px)" }}
              >
                <a
                  href="https://www.facebook.com/lomeditechies.co.za"
                  target="blank"
                >
                  {" "}
                  <Icon
                    as={FaFacebook as React.ElementType}
                    boxSize={4}
                    color="#145DBF"
                  />
                </a>
              </Flex>

              <Flex
                w="34px"
                h="34px"
                borderRadius={cardsBorderRadii}
                align="center"
                justify="center"
                bg={hexToRgba("#E4405F", 0.12)}
                border={`1px solid ${hexToRgba("#E4405F", 0.2)}`}
                cursor="pointer"
                transition="all 0.2s ease"
                _hover={{ transform: "translateY(-1px)" }}
              >
                <Icon
                  as={FaInstagram as React.ElementType}
                  boxSize={4}
                  color="#E4405F"
                />
              </Flex>

              <Flex
                w="34px"
                h="34px"
                borderRadius={cardsBorderRadii}
                align="center"
                justify="center"
                bg={hexToRgba("#1DA1F2", 0.12)}
                border={`1px solid ${hexToRgba("#1DA1F2", 0.2)}`}
                cursor="pointer"
                transition="all 0.2s ease"
                _hover={{ transform: "translateY(-1px)" }}
              >
                <Icon
                  as={FaTwitter as React.ElementType}
                  boxSize={4}
                  color="#1DA1F2"
                />
              </Flex>
            </HStack>

            <GridItem>
              <Heading fontSize="12px" fontWeight="700" color={pageText} mb={2}>
                About Us
              </Heading>
              <Text fontSize="sm" lineHeight="tall" color={subtleText}>
                We bring you the best deals on electronics, fashion, and more.
                Shop smart and earn rewards as you go with Dengu Points!
              </Text>
              <Text fontSize="sm" lineHeight="tall" color={subtleText}>
                <strong> PickDengu only earns when you earn. </strong>
                We charge a small success fee on completed paid orders. There
                are no listing fees, no monthly fees, and no hidden setup costs.
                You keep most of your sale value, while PickDengu helps you
                receive orders, payments, WhatsApp updates, invoices, delivery
                coordination, and customer visibility.
              </Text>
            </GridItem>
          </Box>

          <NotifyModal isOpen={isOpen} onClose={onClose} />
        </VStack>
      </Box>
    </Box>
  );
};

export default SideBar;
